import React, { useState, useEffect, useCallback } from "react";
import { Card, Tabs, Table, Button, Modal, Form, Input, Select, Upload, message, Tooltip } from "antd";
import { PlusOutlined, DeleteOutlined, UploadOutlined, EyeOutlined } from "@ant-design/icons";
import {
  getManualList,
  getAllManualTypes,
  addManual,
  deleteManual,
  addManualType,
  deleteManualType,
  getPreSignedUrl,
} from "./educationApi";
import { convertFileToBase64 } from "./fileUtils";
import { UPLOAD_RULES, uploadHint, uploadProps } from "utils/uploadRules";

const manualUpload = uploadProps(UPLOAD_RULES.MANUAL);

const { TabPane } = Tabs;
const { Option } = Select;
const { confirm } = Modal;

// Rebuilt against a direct re-read of ManualType.js/Manuals.js/AddManual.js/
// AddManualType.js — the manual type's real id field is `equipment_name`
// (not `name`, my first pass's guess), and unlike Survey's upload flow, the
// manual PDF is sent as one base64 field straight to add_equipment_manual —
// no separate upload_certificate round-trip.
export const ManualTypesTab = ({ types, loading, reload }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const { error, result } = await addManualType(values);
      if (error) message.error(result?.message || "Failed to add manual type.");
      else { message.success("Manual type added."); setModalVisible(false); form.resetFields(); reload(); }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (row) => {
    confirm({
      title: "Delete manual type",
      content: `Delete "${row.equipment_name}"?`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const { error, result } = await deleteManualType(row.id);
        if (error) message.error(result?.message || "Failed to delete.");
        else { message.success("Deleted."); reload(); }
      },
    });
  };

  const columns = [
    { title: "Manual Type", dataIndex: "equipment_name", key: "equipment_name" },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)} aria-label="Delete" /></Tooltip>,
    },
  ];

  return (
    <div>
      <div style={{ textAlign: "right", marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>Add New Manual Type</Button>
      </div>
      <Table rowKey={(r, i) => r.id ?? i} columns={columns} dataSource={types} loading={loading} pagination={{ pageSize: 20 }} />
      <Modal title="Add New Manual Type" visible={modalVisible} onCancel={() => setModalVisible(false)} onOk={handleAdd} confirmLoading={saving} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="equipment_name" label="Enter Manual Type" rules={[{ required: true, message: "This field is required." }]}>
            <Input />
          </Form.Item>
          <Form.Item name="display_flag" label="Select Display Flag" rules={[{ required: true, message: "This field is required." }]}>
            <Select>
              <Option value="add">Add</Option>
              <Option value="load_pdf">Load Pdf</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const AddManualModal = ({ visible, types, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [fileObj, setFileObj] = useState(null);

  useEffect(() => { if (visible) { form.resetFields(); setFileObj(null); } }, [visible, form]);

  const handleOk = async () => {
    const values = await form.validateFields();
    if (!fileObj) { message.error("Please select a PDF to upload."); return; }
    setSaving(true);
    try {
      const base64 = await convertFileToBase64(fileObj);
      const { error, result } = await addManual({ ...values, file: base64, filename: fileObj.name });
      if (error) message.error(result?.message || "Failed to add manual.");
      else { message.success("Manual added."); onSaved(values.equipment_manual_type_id); onClose(); }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Add New Manual" visible={visible} onCancel={onClose} onOk={handleOk} confirmLoading={saving} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="manual_name" label="Enter Manual Name" rules={[{ required: true, message: "This field is required." }]}>
          <Input />
        </Form.Item>
        <Form.Item name="equipment_manual_type_id" label="Select Manual Type" rules={[{ required: true, message: "This field is required." }]}>
          <Select showSearch optionFilterProp="children">
            {types.map((t) => <Option key={t.id} value={t.id}>{t.equipment_name}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item label="Manual" required extra={uploadHint(UPLOAD_RULES.MANUAL)}>
          <Upload
            accept={manualUpload.accept}
            maxCount={1}
            beforeUpload={(file) => {
              const result = manualUpload.beforeUpload(file);
              if (result === false) setFileObj(file);
              return result;
            }}
            onRemove={() => setFileObj(null)}
          >
            <Button icon={<UploadOutlined />}>Click to Upload</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export const ManualsTab = ({ types }) => {
  // display_flag === 'add' types are excluded from both the browse filter
  // and the add-form's type selector — confirmed by reading Manuals.js/
  // AddManual.js directly.
  const selectableTypes = types.filter((t) => t.display_flag !== "add");
  const [selectedType, setSelectedType] = useState(null);
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);

  const load = useCallback(async (typeId) => {
    setLoading(true);
    const { error, result } = await getManualList(typeId);
    setManuals(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, []);

  useEffect(() => { load(selectedType); }, [load, selectedType]);

  const handleView = async (row) => {
    const { error, result } = await getPreSignedUrl(row.manual_pdf_link);
    if (!error && result?.result) window.open(result.result, "_blank");
    else message.error("Could not open the manual.");
  };

  const handleDelete = (row) => {
    confirm({
      title: "Delete manual",
      content: `Delete "${row.manual_name}"?`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const { error, result } = await deleteManual(row.id);
        if (error) message.error(result?.message || "Failed to delete.");
        else { message.success("Deleted."); load(selectedType); }
      },
    });
  };

  const columns = [
    { title: "Manual Name", dataIndex: "manual_name", key: "manual_name" },
    {
      title: "Manual Type",
      dataIndex: "equipment_manual_type_id",
      key: "equipment_manual_type_id",
      render: (id) => types.find((t) => t.id === id)?.equipment_name || "-",
    },
    {
      title: "Action",
      key: "action",
      render: (_, row) => (
        <>
          <Tooltip title="View"><Button size="small" icon={<EyeOutlined />} onClick={() => handleView(row)} style={{ marginRight: 8 }} aria-label="View" /></Tooltip>
          <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)} aria-label="Delete" /></Tooltip>
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
        <Select placeholder="All" style={{ width: 240 }} value={selectedType} onChange={setSelectedType} optionFilterProp="label">
          <Option value={null}>All</Option>
          {selectableTypes.map((t) => <Option key={t.id} value={t.id} label={t.equipment_name}>{t.equipment_name}</Option>)}
        </Select>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>Add New Manual</Button>
      </div>
      <Table rowKey={(r, i) => r.id ?? i} columns={columns} dataSource={manuals} loading={loading} pagination={{ pageSize: 20 }} />
      <AddManualModal
        visible={modalVisible}
        types={selectableTypes}
        onClose={() => setModalVisible(false)}
        onSaved={(typeId) => setSelectedType(typeId)}
      />
    </div>
  );
};

// Manual types are shared by the Manuals and Manual Types tabs.
export const useManualTypes = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    setLoading(true);
    const { error, result } = await getAllManualTypes();
    setTypes(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { types, loading, reload };
};

const Manuals = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetched once here (not inside either tab) so both tabs see the same
  // manual-type list regardless of which one is opened first — the old app
  // fetches this independently in each screen's own componentDidMount, which
  // this shared fetch reproduces without duplicating the request per tab.
  const reload = useCallback(async () => {
    setLoading(true);
    const { error, result } = await getAllManualTypes();
    setTypes(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return (
    <Card>
      <Tabs defaultActiveKey="types">
        <TabPane tab="Manual Types" key="types">
          <ManualTypesTab types={types} loading={loading} reload={reload} />
        </TabPane>
        <TabPane tab="Manual" key="manuals">
          <ManualsTab types={types} />
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default Manuals;
